import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useTheme } from '../../contexts/ThemeContext';

export default function ThreeBackground() {
    const mountRef = useRef(null);
    const { isDark } = useTheme();

    useEffect(() => {
        if (!mountRef.current) return;

        // Scene Setup
        const scene = new THREE.Scene();
        const bgColor = isDark ? '#0a0b0d' : '#f8f9fa';
        scene.background = new THREE.Color(bgColor);

        // Camera
        const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        camera.position.z = 30;

        // Renderer
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        mountRef.current.appendChild(renderer.domElement);

        // Mouse tracking
        const mouse = new THREE.Vector2(0, 0);
        const mouseTarget = new THREE.Vector2(0, 0);
        const mouse3D = new THREE.Vector3(0, 0, 0);
        const raycaster = new THREE.Raycaster();

        // Create invisible plane for mouse intersection
        const planeGeometry = new THREE.PlaneGeometry(100, 100);
        const planeMaterial = new THREE.MeshBasicMaterial({ visible: false });
        const plane = new THREE.Mesh(planeGeometry, planeMaterial);
        scene.add(plane);

        // === MAIN PARTICLE SYSTEM ===
        const particleCount = 120; // Reduced for subtlety
        const particles = [];
        const particleGeometry = new THREE.BufferGeometry();
        const positions = new Float32Array(particleCount * 3);
        const colors = new Float32Array(particleCount * 3);
        const sizes = new Float32Array(particleCount);

        // Color palette
        const colorPalette = isDark 
            ? [
                new THREE.Color(0x818cf8), // Indigo
                new THREE.Color(0xa78bfa), // Purple
                new THREE.Color(0x67e8f9), // Cyan
                new THREE.Color(0xfbbf24), // Amber
                new THREE.Color(0xf472b6), // Pink
              ]
            : [
                new THREE.Color(0x6366f1),
                new THREE.Color(0x8b5cf6),
                new THREE.Color(0x06b6d4),
                new THREE.Color(0xf59e0b),
                new THREE.Color(0xec4899),
              ];

        for (let i = 0; i < particleCount; i++) {
            const x = (Math.random() - 0.5) * 60;
            const y = (Math.random() - 0.5) * 40;
            const z = (Math.random() - 0.5) * 20;
            
            positions[i * 3] = x;
            positions[i * 3 + 1] = y;
            positions[i * 3 + 2] = z;

            const color = colorPalette[Math.floor(Math.random() * colorPalette.length)];
            colors[i * 3] = color.r;
            colors[i * 3 + 1] = color.g;
            colors[i * 3 + 2] = color.b;

            sizes[i] = Math.random() * 0.5 + 0.2;

            particles.push({
                originalPos: new THREE.Vector3(x, y, z),
                velocity: new THREE.Vector3(0, 0, 0),
                phase: Math.random() * Math.PI * 2,
            });
        }

        particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        particleGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

        // Custom shader material for glowing particles - very subtle for readability
        const particleMaterial = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                mousePos: { value: new THREE.Vector3(0, 0, 0) },
                pixelRatio: { value: renderer.getPixelRatio() },
            },
            vertexShader: `
                attribute float size;
                attribute vec3 color;
                varying vec3 vColor;
                varying float vAlpha;
                uniform float time;
                uniform vec3 mousePos;
                uniform float pixelRatio;
                
                void main() {
                    vColor = color;
                    
                    vec3 pos = position;
                    float dist = distance(pos, mousePos);
                    float influence = smoothstep(12.0, 0.0, dist);
                    
                    // Push particles away from mouse
                    vec3 direction = normalize(pos - mousePos);
                    pos += direction * influence * 2.5;
                    
                    // Subtle floating motion
                    pos.y += sin(time * 0.5 + position.x * 0.1) * 0.3;
                    pos.x += cos(time * 0.3 + position.y * 0.1) * 0.2;
                    
                    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
                    
                    // Size based on distance and mouse proximity - reduced further
                    float baseSize = size * 6.0;
                    float mouseBoost = influence * 8.0;
                    gl_PointSize = (baseSize + mouseBoost) * pixelRatio * (300.0 / -mvPosition.z);
                    
                    // Much lower base alpha for better text readability
                    vAlpha = 0.15 + influence * 0.2;
                    
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                varying vec3 vColor;
                varying float vAlpha;
                
                void main() {
                    vec2 center = gl_PointCoord - vec2(0.5);
                    float dist = length(center);
                    
                    // Soft glowing circle - much reduced opacity
                    float alpha = smoothstep(0.5, 0.0, dist) * vAlpha;
                    
                    // Subtle glow effect - reduced
                    float glow = exp(-dist * 3.0) * 0.1;
                    
                    gl_FragColor = vec4(vColor, alpha + glow);
                }
            `,
            transparent: true,
            blending: THREE.NormalBlending,
            depthWrite: false,
        });

        const particleSystem = new THREE.Points(particleGeometry, particleMaterial);
        scene.add(particleSystem);

        // === MOUSE TRAIL ===
        const trailLength = 50;
        const trailPositions = new Float32Array(trailLength * 3);
        const trailGeometry = new THREE.BufferGeometry();
        trailGeometry.setAttribute('position', new THREE.BufferAttribute(trailPositions, 3));

        const trailMaterial = new THREE.ShaderMaterial({
            uniforms: {
                color: { value: new THREE.Color(isDark ? 0x818cf8 : 0x6366f1) },
            },
            vertexShader: `
                attribute float alpha;
                varying float vAlpha;
                void main() {
                    vAlpha = alpha;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform vec3 color;
                varying float vAlpha;
                void main() {
                    gl_FragColor = vec4(color, vAlpha * 0.2);
                }
            `,
            transparent: true,
            blending: THREE.NormalBlending,
        });

        const trailAlphas = new Float32Array(trailLength);
        for (let i = 0; i < trailLength; i++) {
            trailAlphas[i] = 1 - i / trailLength;
        }
        trailGeometry.setAttribute('alpha', new THREE.BufferAttribute(trailAlphas, 1));

        const trail = new THREE.Line(trailGeometry, trailMaterial);
        scene.add(trail);

        const trailHistory = [];

        // === FLOATING NOTES (3D) ===
        const noteCount = 12;
        const notes = [];
        const noteGroup = new THREE.Group();

        const noteGeometry = new THREE.PlaneGeometry(2, 1.5);
        
        for (let i = 0; i < noteCount; i++) {
            const color = colorPalette[i % colorPalette.length];
            const noteMaterial = new THREE.MeshBasicMaterial({
                color: color,
                transparent: true,
                opacity: isDark ? 0.15 : 0.12,
                side: THREE.DoubleSide,
            });

            const note = new THREE.Mesh(noteGeometry, noteMaterial);
            note.position.x = (Math.random() - 0.5) * 50;
            note.position.y = (Math.random() - 0.5) * 30;
            note.position.z = (Math.random() - 0.5) * 15 - 5;
            
            note.rotation.x = Math.random() * 0.3;
            note.rotation.y = Math.random() * 0.3;
            note.rotation.z = (Math.random() - 0.5) * 0.2;

            note.userData = {
                originalPos: note.position.clone(),
                phase: Math.random() * Math.PI * 2,
                speed: Math.random() * 0.5 + 0.5,
            };

            notes.push(note);
            noteGroup.add(note);
        }
        scene.add(noteGroup);

        // === CONNECTION LINES ===
        const connectionMaterial = new THREE.LineBasicMaterial({
            color: isDark ? 0x818cf8 : 0x6366f1,
            transparent: true,
            opacity: 0.1,
        });

        const connectionsGroup = new THREE.Group();
        scene.add(connectionsGroup);

        // === CENTRAL ORB (follows mouse) ===
        const orbGeometry = new THREE.SphereGeometry(0.8, 32, 32);
        const orbMaterial = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                color1: { value: new THREE.Color(isDark ? 0x818cf8 : 0x6366f1) },
                color2: { value: new THREE.Color(isDark ? 0xf472b6 : 0xec4899) },
            },
            vertexShader: `
                varying vec3 vNormal;
                varying vec3 vPosition;
                void main() {
                    vNormal = normal;
                    vPosition = position;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform float time;
                uniform vec3 color1;
                uniform vec3 color2;
                varying vec3 vNormal;
                varying vec3 vPosition;
                
                void main() {
                    float fresnel = pow(1.0 - abs(dot(vNormal, vec3(0.0, 0.0, 1.0))), 2.0);
                    vec3 color = mix(color1, color2, sin(time * 2.0 + vPosition.y * 3.0) * 0.5 + 0.5);
                    float alpha = fresnel * 0.5 + 0.15;
                    gl_FragColor = vec4(color, alpha);
                }
            `,
            transparent: true,
            blending: THREE.NormalBlending,
        });

        const orb = new THREE.Mesh(orbGeometry, orbMaterial);
        orb.position.z = 5;
        scene.add(orb);

        // Orb glow
        const glowGeometry = new THREE.SphereGeometry(1.5, 32, 32);
        const glowMaterial = new THREE.ShaderMaterial({
            uniforms: {
                color: { value: new THREE.Color(isDark ? 0x818cf8 : 0x6366f1) },
            },
            vertexShader: `
                varying vec3 vNormal;
                void main() {
                    vNormal = normal;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform vec3 color;
                varying vec3 vNormal;
                void main() {
                    float intensity = pow(0.6 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
                    gl_FragColor = vec4(color, intensity * 0.15);
                }
            `,
            transparent: true,
            blending: THREE.NormalBlending,
            side: THREE.BackSide,
        });

        const glow = new THREE.Mesh(glowGeometry, glowMaterial);
        orb.add(glow);

        // === EVENT HANDLERS ===
        const handleMouseMove = (event) => {
            mouseTarget.x = (event.clientX / window.innerWidth) * 2 - 1;
            mouseTarget.y = -(event.clientY / window.innerHeight) * 2 + 1;
        };

        window.addEventListener('mousemove', handleMouseMove);

        // === ANIMATION LOOP ===
        let time = 0;
        let animationFrameId;

        const animate = () => {
            animationFrameId = requestAnimationFrame(animate);
            time += 0.016;

            // Smooth mouse interpolation
            mouse.x += (mouseTarget.x - mouse.x) * 0.08;
            mouse.y += (mouseTarget.y - mouse.y) * 0.08;

            // Update raycaster for 3D mouse position
            raycaster.setFromCamera(mouse, camera);
            const intersects = raycaster.intersectObject(plane);
            if (intersects.length > 0) {
                mouse3D.copy(intersects[0].point);
            }

            // Update particle shader uniforms
            particleMaterial.uniforms.time.value = time;
            particleMaterial.uniforms.mousePos.value.copy(mouse3D);

            // Update orb material
            orbMaterial.uniforms.time.value = time;

            // Move orb towards mouse
            orb.position.x += (mouse3D.x * 0.7 - orb.position.x) * 0.05;
            orb.position.y += (mouse3D.y * 0.7 - orb.position.y) * 0.05;
            orb.rotation.x = time * 0.3;
            orb.rotation.y = time * 0.5;

            // Update trail
            trailHistory.unshift(orb.position.clone());
            if (trailHistory.length > trailLength) {
                trailHistory.pop();
            }

            const trailPos = trailGeometry.attributes.position.array;
            for (let i = 0; i < trailLength; i++) {
                if (trailHistory[i]) {
                    trailPos[i * 3] = trailHistory[i].x;
                    trailPos[i * 3 + 1] = trailHistory[i].y;
                    trailPos[i * 3 + 2] = trailHistory[i].z;
                }
            }
            trailGeometry.attributes.position.needsUpdate = true;

            // Animate floating notes
            notes.forEach((note, i) => {
                const userData = note.userData;
                
                // Floating motion
                note.position.x = userData.originalPos.x + Math.sin(time * userData.speed + userData.phase) * 2;
                note.position.y = userData.originalPos.y + Math.cos(time * userData.speed * 0.7 + userData.phase) * 1.5;
                
                // Subtle rotation
                note.rotation.z = Math.sin(time * 0.5 + userData.phase) * 0.1;
                
                // React to mouse
                const dist = note.position.distanceTo(mouse3D);
                if (dist < 10) {
                    const influence = (10 - dist) / 10;
                    const direction = new THREE.Vector3().subVectors(note.position, mouse3D).normalize();
                    note.position.add(direction.multiplyScalar(influence * 0.5));
                    note.material.opacity = (isDark ? 0.15 : 0.12) + influence * 0.15;
                } else {
                    note.material.opacity = isDark ? 0.15 : 0.12;
                }
            });

            // Update connections (every 5 frames for performance)
            if (Math.floor(time * 60) % 5 === 0) {
                while (connectionsGroup.children.length > 0) {
                    const child = connectionsGroup.children[0];
                    connectionsGroup.remove(child);
                    child.geometry.dispose();
                }

                // Connect orb to nearby notes
                notes.forEach(note => {
                    const dist = orb.position.distanceTo(note.position);
                    if (dist < 15) {
                        const opacity = (1 - dist / 15) * (isDark ? 0.15 : 0.1);
                        const lineMat = new THREE.LineBasicMaterial({
                            color: isDark ? 0x818cf8 : 0x6366f1,
                            transparent: true,
                            opacity: opacity,
                        });
                        const points = [orb.position.clone(), note.position.clone()];
                        const lineGeom = new THREE.BufferGeometry().setFromPoints(points);
                        const line = new THREE.Line(lineGeom, lineMat);
                        connectionsGroup.add(line);
                    }
                });
            }

            // Subtle camera movement
            camera.position.x = mouse.x * 2;
            camera.position.y = mouse.y * 1.5;
            camera.lookAt(0, 0, 0);

            renderer.render(scene, camera);
        };

        animate();

        // === RESIZE HANDLER ===
        const handleResize = () => {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };

        window.addEventListener('resize', handleResize);

        // === CLEANUP ===
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);
            
            if (mountRef.current && renderer.domElement) {
                mountRef.current.removeChild(renderer.domElement);
            }

            // Dispose geometries and materials
            particleGeometry.dispose();
            particleMaterial.dispose();
            trailGeometry.dispose();
            trailMaterial.dispose();
            noteGeometry.dispose();
            notes.forEach(n => n.material.dispose());
            orbGeometry.dispose();
            orbMaterial.dispose();
            glowGeometry.dispose();
            glowMaterial.dispose();
            planeGeometry.dispose();
            planeMaterial.dispose();
            connectionMaterial.dispose();
            
            connectionsGroup.children.forEach(child => {
                child.geometry.dispose();
                child.material.dispose();
            });

            renderer.dispose();
        };
    }, [isDark]);

    return <div ref={mountRef} style={{ width: '100%', height: '100%' }} />;
}
